import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  FileCode2, RefreshCw, Copy, Trash2, CopyPlus,
  Table2, Eye, Columns3, DatabaseZap, Download, Upload,
  Pencil, Eraser, Plus, Wrench, Braces, GitBranchPlus, GitCompareArrows, FileUp,
} from 'lucide-react';
import { apiClient } from '@/api/client';
import { useConnectionStore } from '@/stores/connection-store';
import { useAppStore } from '@/stores/app-store';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { useDialogStore } from '@/stores/dialog-store';
import { useToastStore } from '@/stores/toast-store';
import { ContextMenu, type ContextMenuItem } from '@/components/shared/ContextMenu';
import { buildConnectionActions, type ConnectionActionDeps } from '@/lib/actions/connection-actions';
import { buildDatabaseActions } from '@/lib/actions/database-actions';
import { getDatabaseActionDeps } from '@/lib/actions/database-deps';
import { buildTableActions } from '@/lib/actions/table-actions';
import { getTableActionDeps } from '@/lib/actions/table-deps';
import { isDatabaseVisible } from '@/lib/database-visibility';
import { resolveVisibleSchemas } from '@/lib/schema-visibility';
import { useModalStore } from '@/stores/modal-store';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { ImportModal } from '@/components/modals/ImportModal';
import { TreeNode } from './TreeNode';
import { SkeletonTreeRows } from '@/components/shared/Skeleton';
import type {
  ConnectionConfig,
  SchemaMetadata,
  ColumnMeta,
  ConnectionStatus,
  DatabaseInfo,
  SavedQuery,
} from '@/types';

interface ContextMenuState {
  position: { x: number; y: number };
  items: ContextMenuItem[];
}

interface ConnectionTreeProps {
  connections: ConnectionConfig[];
  searchFilter: string;
  onEditConnection?: (connection: ConnectionConfig) => void;
}

/** Tracks which tree nodes are expanded by their path key. */
type ExpandedMap = Record<string, boolean>;

/** Trigger a download of a .sql file from a string */
function downloadSQL(filename: string, sql: string): void {
  const blob = new Blob([sql], { type: 'text/sql' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

interface ConfirmDialogState {
  title: string;
  message: string;
  confirmText: string;
  confirmLabel?: string;
  danger?: boolean;
  onConfirm: () => Promise<void>;
}

export function ConnectionTree({ connections, searchFilter, onEditConnection }: ConnectionTreeProps) {
  const expanded = useAppStore((s) => s.treeExpanded);
  const toggleTreeNode = useAppStore((s) => s.toggleTreeNode);
  const setTreeExpanded = useAppStore((s) => s.setTreeExpanded);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  // Database modal lives in useModalStore so InfoView/mobile/command-palette
  // can open it too. Use the store actions when a caller (e.g., the connection
  // context menu's "Create Database…") wants to open it.
  const openCreateDatabase = useModalStore((s) => s.openCreateDatabase);
  const [importModal, setImportModal] = useState<{
    connectionId: string;
    schema: string;
    tableName?: string;
    tableColumns?: { name: string; type: string }[];
  } | null>(null);
  // Sharing dialog is opened via useModalStore so the dialog itself can be
  // mounted once at the app shell and reused by InfoView, TunnelView, and
  // the mobile screens. Action-builder onShare callbacks call the store.
  const openShareDialog = useModalStore((s) => s.openShareDialog);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const restoreConnectionIdRef = useRef<string>('');
  const queryClient = useQueryClient();
  const activeConnections = useConnectionStore((s) => s.activeConnections);
  const setStatus = useConnectionStore((s) => s.setStatus);
  const openTab = useWorkspaceStore((s) => s.openTab);

  const toggle = useCallback((key: string) => {
    toggleTreeNode(key);
  }, [toggleTreeNode]);

  const closeContextMenu = useCallback(() => setContextMenu(null), []);

  const connectMutation = useMutation({
    mutationFn: (connectionId: string) =>
      apiClient.post<{ ok: boolean }>(`/api/connections/${connectionId}/connect`),
    onMutate: (connectionId) => {
      setStatus(connectionId, 'connecting');
    },
    onSuccess: (_data, connectionId) => {
      setStatus(connectionId, 'connected');
      // Invalidate schema so it refetches
      queryClient.invalidateQueries({ queryKey: ['schema', connectionId] });
    },
    onError: (err, connectionId) => {
      const message = err instanceof Error ? err.message : 'Connection failed';
      setStatus(connectionId, 'error', message);
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: (connectionId: string) =>
      apiClient.post<{ ok: boolean }>(`/api/connections/${connectionId}/disconnect`),
    onSuccess: (_data, connectionId) => {
      setStatus(connectionId, 'disconnected');
    },
    onError: (err, connectionId) => {
      const message = err instanceof Error ? err.message : 'Disconnect failed';
      setStatus(connectionId, 'error', message);
    },
  });

  const deleteConnectionMutation = useMutation({
    mutationFn: (connectionId: string) =>
      apiClient.delete<{ ok: boolean }>(`/api/connections/${connectionId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['connections'] });
    },
  });

  const buildConnectionActionsDeps = useCallback(
    (conn: ConnectionConfig): ConnectionActionDeps => ({
      onConnect: () => {
        connectMutation.mutate(conn.id);
        setTreeExpanded({ ...expanded, [`conn:${conn.id}`]: true });
      },
      onDisconnect: () => disconnectMutation.mutate(conn.id),
      onNewQuery: () => {
        openTab({
          type: 'query',
          id: `query:${conn.id}:${Date.now()}`,
          connectionId: conn.id,
          title: `Query - ${conn.name}`,
          sql: '',
        });
      },
      onReload: async () => {
        // Connection-level reload: refetch the database list AND drop every
        // per-database schema cache entry so each expanded DB re-fetches
        // fresh metadata. Uses ?refresh=1 to bypass the server cache.
        try {
          await apiClient.post(`/api/connections/${conn.id}/databases/refresh`);
        } catch { /* toast handled below if needed */ }
        queryClient.invalidateQueries({ queryKey: ['databases', conn.id] });
        queryClient.invalidateQueries({ queryKey: ['schema', conn.id] });
      },
      onCreateDatabase: () => {
        openCreateDatabase(conn.id);
      },
      onEdit: () => onEditConnection?.(conn),
      onRename: async () => {
        const newName = await useDialogStore.getState().showPrompt({
          title: 'Rename Connection',
          defaultValue: conn.name,
        });
        if (newName?.trim() && newName.trim() !== conn.name) {
          apiClient
            .put(`/api/connections/${conn.id}`, { name: newName.trim() })
            .then(() => queryClient.invalidateQueries({ queryKey: ['connections'] }))
            .catch((err: unknown) => {
              const msg = err instanceof Error ? err.message : 'Rename failed';
              useToastStore.getState().addToast('error', msg);
            });
        }
      },
      onCopyHostPort: () => {
        navigator.clipboard.writeText(`${conn.host}:${conn.port}`);
      },
      onShare: () => openShareDialog({
        resourceType: 'connection',
        resourceId: conn.id,
        resourceName: conn.name,
      }),
      onDelete: () => {
        useDialogStore.getState().showConfirm({
          title: 'Delete Connection',
          message: `This will permanently delete the connection "${conn.name}" and all of its saved queries. The remote database itself is untouched. This action cannot be undone.`,
          confirmText: conn.name,
          danger: true,
          onConfirm: async () => {
            await new Promise<void>((resolve, reject) => {
              deleteConnectionMutation.mutate(conn.id, {
                onSuccess: () => resolve(),
                onError: (err) => reject(err instanceof Error ? err : new Error(String(err))),
              });
            });
          },
        });
      },
    }),
    [
      connectMutation, disconnectMutation, deleteConnectionMutation,
      openTab, queryClient, onEditConnection, setTreeExpanded, expanded,
      openShareDialog,
    ],
  );

  const handleConnectionContextMenu = useCallback(
    (e: React.MouseEvent, conn: ConnectionConfig) => {
      e.preventDefault();
      e.stopPropagation();
      const status = activeConnections[conn.id]?.status ?? 'disconnected';
      const items = buildConnectionActions(conn, status, buildConnectionActionsDeps(conn));
      setContextMenu({ position: { x: e.clientX, y: e.clientY }, items });
    },
    [activeConnections, buildConnectionActionsDeps],
  );

  const handleConnectionToggle = useCallback(
    (conn: ConnectionConfig) => {
      const key = `conn:${conn.id}`;
      const status = activeConnections[conn.id]?.status;
      const isExpanded = expanded[key] ?? false;

      // A chevron click on an already-expanded node always means "collapse"
      // regardless of connect status. Without this guard, clicking collapse
      // right after Disconnect would re-trigger connect() because the
      // fallthrough branch below would fire — surprising behavior reported
      // by users who expected the tree to just fold.
      if (isExpanded) {
        toggle(key);
        return;
      }

      if (status === 'connected') {
        // Expanding an already-connected node is just a tree toggle.
        toggle(key);
      } else if (status === 'connecting') {
        // Ignore clicks while connecting.
        return;
      } else {
        // Collapsed + disconnected → connect, then expand once it's ready.
        connectMutation.mutate(conn.id);
        setTreeExpanded({ ...expanded, [key]: true });
      }
    },
    [activeConnections, connectMutation, expanded, setTreeExpanded, toggle],
  );

  const handleConnectionClick = useCallback(
    (conn: ConnectionConfig) => {
      openTab({
        type: 'info',
        id: `info:conn:${conn.id}`,
        connectionId: conn.id,
        objectType: 'connection',
        objectName: conn.name,
      });
    },
    [openTab],
  );

  const handleDatabaseClick = useCallback(
    (connectionId: string, dbName: string) => {
      openTab({
        type: 'info',
        id: `info:db:${connectionId}:${dbName}`,
        connectionId,
        database: dbName,
        objectType: 'database',
        objectName: dbName,
      });
    },
    [openTab],
  );

  const handleTableClick = useCallback(
    (connectionId: string, schema: string, tableName: string, database?: string) => {
      // `database` is the DB the table lives in — set when opening a non-default
      // database's table so the tab's queries route to the right pool. The tab
      // id includes the database so the same table in two different DBs opens
      // as two separate tabs instead of reusing one.
      const tabId = database
        ? `table:${connectionId}:${database}:${schema}.${tableName}`
        : `table:${connectionId}:${schema}.${tableName}`;
      openTab({
        type: 'table',
        id: tabId,
        connectionId,
        database,
        schema,
        table: tableName,
        where: '',
        orderBy: '',
      });
    },
    [openTab],
  );

  // Search is a global tree filter. Keep every connection visible while a
  // search term is active so matches in any connection's cached schema can
  // surface — the nested nodes (DatabaseSubtree / FolderNodes / …) filter
  // themselves. Hiding at this level would mean "user doesn't remember which
  // connection the table lives in" returns nothing, which is the opposite of
  // what they wanted.
  const filtered = connections;
  const isSearching = searchFilter.length > 0;

  if (filtered.length === 0) {
    return null;
  }

  return (
    <>
      <div className="py-0.5">
        {filtered.map((conn) => {
          const connKey = `conn:${conn.id}`;
          const status: ConnectionStatus =
            activeConnections[conn.id]?.status ?? 'disconnected';
          // Force-expand every connection while the user is searching so the
          // schema tree renders and can match tables/columns across all
          // connections. Without this, search silently "does nothing" on
          // collapsed connections.
          const isExpanded = isSearching ? true : (expanded[connKey] ?? false);
          const isConnected = status === 'connected';

          return (
            <div key={conn.id}>
              <TreeNode
                label={conn.name}
                depth={0}
                icon="connection"
                expanded={isExpanded}
                hasChildren
                statusDot={status === 'connecting' ? 'connecting' : status}
                isConnecting={status === 'connecting'}
                accentColor={conn.colorTag}
                highlight={searchFilter}
                treeKey={connKey}
                sharedBadge={conn.shared}
                onClick={() => handleConnectionClick(conn)}
                onToggle={() => handleConnectionToggle(conn)}
                onContextMenu={(e) => handleConnectionContextMenu(e, conn)}
              />
              {isExpanded && (
                // Saved queries live in DBGo's own SQLite, not in the remote
                // database — so they're shown even when the connection isn't
                // open yet. Clicking a saved query auto-connects.
                <SavedQueriesFolder
                  connectionId={conn.id}
                  expanded={expanded}
                  toggle={toggle}
                  searchFilter={searchFilter}
                  onShowContextMenu={setContextMenu}
                />
              )}
              {/* SchemaTree renders when the connection is either connected
                  or just disconnected (read from cache). We DO NOT render it
                  in the 'error' or 'connecting' branches — those get a
                  dedicated inline status row below so we don't double up
                  the error messages (one from the failed fetch + one from
                  the connection-status store). */}
              {isExpanded && status !== 'error' && status !== 'connecting' && (
                <SchemaTree
                  connectionId={conn.id}
                  expanded={expanded}
                  toggle={toggle}
                  searchFilter={searchFilter}
                  onDatabaseClick={handleDatabaseClick}
                  onTableClick={handleTableClick}
                  onShowContextMenu={setContextMenu}
                  onShowConfirmDialog={setConfirmDialog}
                  onTriggerRestore={(cId) => {
                    restoreConnectionIdRef.current = cId;
                    fileInputRef.current?.click();
                  }}
                  onShowImportModal={setImportModal}
                />
              )}
              {isExpanded && status === 'connecting' && (
                <div className="py-1 pl-10">
                  <span className="text-[11px] text-text-muted italic">
                    Connecting…
                  </span>
                </div>
              )}
              {isExpanded && status === 'error' && (
                <ConnectionErrorRow
                  connectionId={conn.id}
                  message={activeConnections[conn.id]?.error ?? 'Connection failed'}
                  onRetry={() => connectMutation.mutate(conn.id)}
                  onEdit={onEditConnection ? () => onEditConnection(conn) : undefined}
                />
              )}
            </div>
          );
        })}
      </div>

      {contextMenu && (
        <ContextMenu
          items={contextMenu.items}
          position={contextMenu.position}
          onClose={closeContextMenu}
        />
      )}

      {confirmDialog && (
        <ConfirmDialog
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmText={confirmDialog.confirmText}
          confirmLabel={confirmDialog.confirmLabel}
          danger={confirmDialog.danger}
          loading={confirmLoading}
          onCancel={() => {
            setConfirmDialog(null);
            setConfirmLoading(false);
          }}
          onConfirm={async () => {
            setConfirmLoading(true);
            try {
              await confirmDialog.onConfirm();
              setConfirmDialog(null);
            } catch (err: unknown) {
              const msg = err instanceof Error ? err.message : 'Operation failed';
              useToastStore.getState().addToast('error', msg);
            } finally {
              setConfirmLoading(false);
            }
          }}
        />
      )}

      {/* DatabaseModal is rendered at the app shell (AppShell / MobileShell)
          and its open/close state lives in useModalStore. */}

      {importModal && (
        <ImportModal
          connectionId={importModal.connectionId}
          schema={importModal.schema}
          tableName={importModal.tableName}
          tableColumns={importModal.tableColumns}
          onClose={() => setImportModal(null)}
          onImported={() => {
            queryClient.invalidateQueries({ queryKey: ['schema', importModal.connectionId] });
            setImportModal(null);
          }}
        />
      )}


      {/* Hidden file input for SQL restore */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".sql"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          const connectionId = restoreConnectionIdRef.current;
          const reader = new FileReader();
          reader.onload = () => {
            const sql = reader.result as string;
            apiClient
              .post('/api/query/execute-multi', { connectionId, sql })
              .then(() => {
                queryClient.invalidateQueries({ queryKey: ['schema', connectionId] });
              })
              .catch((err: unknown) => {
                const msg = err instanceof Error ? err.message : 'Restore failed';
                useToastStore.getState().addToast('error', msg);
              });
          };
          reader.readAsText(file);
          // Reset so the same file can be selected again
          e.target.value = '';
        }}
      />
    </>
  );
}

// ─── Schema subtree (fetched per-connection) ─────────────────

interface SchemaTreeProps {
  connectionId: string;
  expanded: ExpandedMap;
  toggle: (key: string) => void;
  searchFilter: string;
  onDatabaseClick: (connectionId: string, dbName: string) => void;
  onTableClick: (connectionId: string, schema: string, tableName: string, database?: string) => void;
  onShowContextMenu: (state: ContextMenuState) => void;
  onShowConfirmDialog: (state: ConfirmDialogState) => void;
  onTriggerRestore: (connectionId: string) => void;
  onShowImportModal: (state: {
    connectionId: string;
    schema: string;
    tableName?: string;
    tableColumns?: { name: string; type: string }[];
  }) => void;
}

function SchemaTree({
  connectionId,
  expanded,
  toggle,
  searchFilter,
  onDatabaseClick,
  onTableClick,
  onShowContextMenu,
  onShowConfirmDialog,
  onTriggerRestore,
  onShowImportModal,
}: SchemaTreeProps) {
  const queryClient = useQueryClient();
  const openTab = useWorkspaceStore((s) => s.openTab);
  // User's per-connection visibility overrides; re-renders the tree the
  // instant a checkbox is toggled on the Connection info tab.
  const visibilityOverrides = useAppStore((s) => s.databaseVisibility[connectionId]);

  // Enumerate every database the credentials can reach, then render each as
  // a lazy-loading node (see DatabaseSubtree). Per-DB schema is fetched on
  // first expand only, keeping the initial tree cheap even on servers with
  // dozens of databases. Connection-level state (connecting / failed) is
  // surfaced by the sidebar Connection node above, not here.
  const { data: databases, isLoading, error } = useQuery<DatabaseInfo[]>({
    queryKey: ['databases', connectionId],
    queryFn: () => apiClient.get<DatabaseInfo[]>(`/api/connections/${connectionId}/databases`),
    staleTime: 60_000,
  });

  if (isLoading) return <SkeletonTreeRows count={4} />;
  if (error) {
    // Strip the server's "Failed to list databases: " prefix — it's
    // duplicative here since the user is already looking at a databases
    // section. Show the underlying cause compactly with the full text
    // available on hover.
    const raw = error instanceof Error ? error.message : 'Failed to load databases';
    const msg = raw.replace(/^Failed to list databases:\s*/i, '');
    return (
      <div className="mx-2 my-1 px-2 py-1.5 rounded-sm bg-accent-red/10 border border-accent-red/20">
        <span className="text-[11px] text-accent-red truncate block" title={raw}>
          {msg}
        </span>
      </div>
    );
  }
  if (!databases?.length) {
    return (
      <div className="py-1 pl-10">
        <span className="text-[11px] text-text-muted">No databases found</span>
      </div>
    );
  }

  const visible = databases.filter((d) => isDatabaseVisible(d, visibilityOverrides));

  return (
    <>
      {visible.map((dbInfo) => (
        <DatabaseSubtree
          key={dbInfo.name}
          connectionId={connectionId}
          dbInfo={dbInfo}
          expanded={expanded}
          toggle={toggle}
          searchFilter={searchFilter}
          onDatabaseClick={onDatabaseClick}
          onTableClick={onTableClick}
          onShowContextMenu={onShowContextMenu}
          onShowConfirmDialog={onShowConfirmDialog}
          onTriggerRestore={onTriggerRestore}
          onShowImportModal={onShowImportModal}
        />
      ))}
    </>
  );
}

// ─── DatabaseSubtree: one database node + its lazy-loaded schema ────

interface DatabaseSubtreeProps {
  connectionId: string;
  dbInfo: DatabaseInfo;
  expanded: ExpandedMap;
  toggle: (key: string) => void;
  searchFilter: string;
  onDatabaseClick: (connectionId: string, dbName: string) => void;
  onTableClick: (connectionId: string, schema: string, tableName: string, database?: string) => void;
  onShowContextMenu: (state: ContextMenuState) => void;
  onShowConfirmDialog: (state: ConfirmDialogState) => void;
  onTriggerRestore: (connectionId: string) => void;
  onShowImportModal: (state: {
    connectionId: string;
    schema: string;
    tableName?: string;
    tableColumns?: { name: string; type: string }[];
  }) => void;
}

function DatabaseSubtree({
  connectionId,
  dbInfo,
  expanded,
  toggle,
  searchFilter,
  onDatabaseClick,
  onTableClick,
  onShowContextMenu,
  onShowConfirmDialog,
  onTriggerRestore,
  onShowImportModal,
}: DatabaseSubtreeProps) {
  const queryClient = useQueryClient();
  const openTab = useWorkspaceStore((s) => s.openTab);
  const dbKey = `db:${connectionId}:${dbInfo.name}`;
  const isSearching = searchFilter.length > 0;
  // Default DBs start expanded so the common case (single-DB user) looks
  // unchanged from before multi-DB support landed.
  const defaultExpanded = dbInfo.isDefault ?? false;
  const dbExpanded = isSearching ? true : (expanded[dbKey] ?? defaultExpanded);

  const { data: schema, isLoading: schemaLoading } = useQuery<SchemaMetadata>({
    queryKey: ['schema', connectionId, dbInfo.name],
    queryFn: () => apiClient.get<SchemaMetadata>(
      `/api/schema/${connectionId}?database=${encodeURIComponent(dbInfo.name)}`,
    ),
    enabled: dbExpanded,
    staleTime: 60_000,
  });

  // For multi-DB drivers the server returns a single DB entry matching what
  // we asked for; for MySQL-style drivers the one DB contains schemas == dbs
  // — either way we only render the first entry's schemas under this node.
  const allDbSchemas = schema?.databases?.[0]?.schemas ?? [];
  // Per-(connection, database) schema visibility overrides. For Oracle the
  // driver marks the user's own schema as `isDefault`; without an override
  // we show only that one, matching how databases are filtered on the
  // connection level. Other drivers leave isDefault unset on every schema,
  // and `resolveVisibleSchemas` returns the full list unchanged.
  const schemaOverrides = useAppStore((s) => s.schemaVisibility[connectionId]?.[dbInfo.name]);
  const dbSchemas = resolveVisibleSchemas(allDbSchemas, schemaOverrides);

  const lf = searchFilter.toLowerCase();
  const schemaHasMatch = (s: SchemaMetadata['databases'][0]['schemas'][0]) => {
    if (!isSearching) return true;
    if (s.name.toLowerCase().includes(lf)) return true;
    if (s.tables.some((t) => t.name.toLowerCase().includes(lf))) return true;
    if (s.views.some((v) => v.name.toLowerCase().includes(lf))) return true;
    if (s.functions.some((f) => f.name.toLowerCase().includes(lf))) return true;
    if (s.tables.some((t) => t.columns?.some((c) => c.name.toLowerCase().includes(lf)))) return true;
    return false;
  };

  const handleDatabaseContextMenu = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const defaultSchema = dbSchemas.length > 0 ? dbSchemas[0].name : 'public';
      const items = buildDatabaseActions(
        getDatabaseActionDeps({
          connectionId,
          dbName: dbInfo.name,
          defaultSchema,
          openTab,
          queryClient,
          onTriggerRestore,
          onShowImportModal,
        }),
      );
      onShowContextMenu({ position: { x: e.clientX, y: e.clientY }, items });
    },
    [connectionId, dbInfo.name, dbSchemas, openTab, queryClient, onTriggerRestore, onShowImportModal, onShowContextMenu],
  );

  return (
    <div>
      <TreeNode
        label={dbInfo.name}
        depth={1}
        icon="database"
        expanded={dbExpanded}
        hasChildren
        highlight={searchFilter}
        treeKey={dbKey}
        onClick={() => onDatabaseClick(connectionId, dbInfo.name)}
        onToggle={() => toggle(dbKey)}
        onContextMenu={handleDatabaseContextMenu}
      />
      {dbExpanded && schemaLoading && (
        <div className="pl-10 py-1">
          <span className="text-[11px] text-text-muted">Loading schema…</span>
        </div>
      )}
      {dbExpanded && !schemaLoading && (() => {
        const filteredSchemas = dbSchemas.filter(schemaHasMatch);

        // Flatten redundant schema-under-database nesting. Two cases:
        //   1. Oracle "schemas-are-databases": exactly one schema whose
        //      name matches the DB. Tree shape matches DataGrip's.
        //   2. Schemaless drivers (MongoDB): exactly one schema with an
        //      empty name. Driver returns the synthetic schema as a
        //      container; the user thinks of it as `database → collections`,
        //      not `database → schema → collections`.
        if (
          filteredSchemas.length === 1 &&
          (filteredSchemas[0].name === '' ||
            filteredSchemas[0].name.toLowerCase() === dbInfo.name.toLowerCase())
        ) {
          const s = filteredSchemas[0];
          return (
            <FolderNodes
              connectionId={connectionId}
              dbName={dbInfo.name}
              schemaName={s.name}
              tables={s.tables}
              views={s.views}
              functions={s.functions}
              expanded={expanded}
              toggle={toggle}
              searchFilter={searchFilter}
              onTableClick={onTableClick}
              onShowContextMenu={onShowContextMenu}
              onShowConfirmDialog={onShowConfirmDialog}
              onShowImportModal={onShowImportModal}
            />
          );
        }

        return filteredSchemas.map((s) => {
          const schemaKey = `schema:${connectionId}:${dbInfo.name}:${s.name}`;
          const schemaExpanded = isSearching ? true : (expanded[schemaKey] ?? false);

          return (
            <div key={schemaKey}>
              <TreeNode
                label={s.name}
                depth={2}
                icon="schema"
                expanded={schemaExpanded}
                hasChildren={
                  s.tables.length > 0 ||
                  s.views.length > 0 ||
                  s.functions.length > 0
                }
                highlight={searchFilter}
                treeKey={schemaKey}
                onToggle={() => toggle(schemaKey)}
                onContextMenu={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onShowContextMenu({
                    position: { x: e.clientX, y: e.clientY },
                    items: [
                      {
                        label: 'Create Table...',
                        icon: Plus,
                        onClick: () => {
                          openTab({
                            type: 'designer',
                            id: `designer:${connectionId}:${dbInfo.name}:${s.name}:new:${Date.now()}`,
                            connectionId,
                            database: dbInfo.name,
                            schema: s.name,
                            mode: 'create',
                          });
                        },
                      },
                      {
                        label: 'Create View...',
                        icon: Eye,
                        onClick: () => {
                          openTab({
                            type: 'code-editor',
                            id: `code-editor:${connectionId}:${dbInfo.name}:${s.name}:new-view:${Date.now()}`,
                            connectionId,
                            database: dbInfo.name,
                            schema: s.name,
                            objectName: 'new_view',
                            objectType: 'view',
                            mode: 'create',
                          });
                        },
                      },
                      {
                        label: 'Create Function...',
                        icon: Braces,
                        onClick: () => {
                          openTab({
                            type: 'code-editor',
                            id: `code-editor:${connectionId}:${dbInfo.name}:${s.name}:new-func:${Date.now()}`,
                            connectionId,
                            database: dbInfo.name,
                            schema: s.name,
                            objectName: 'new_function',
                            objectType: 'function',
                            mode: 'create',
                          });
                        },
                      },
                      { label: '', separator: true },
                      {
                        label: 'Reload',
                        icon: RefreshCw,
                        onClick: () => {
                          // Bypass the server schema cache (?refresh=1); a plain
                          // invalidate refetches without it and reads stale cache.
                          void (async () => {
                            try {
                              const fresh = await apiClient.get(
                                `/api/schema/${connectionId}?database=${encodeURIComponent(dbInfo.name)}&refresh=1`,
                              );
                              queryClient.setQueryData(['schema', connectionId, dbInfo.name], fresh);
                            } catch (err: unknown) {
                              useToastStore.getState().addToast('error', err instanceof Error ? err.message : 'Reload failed');
                            }
                          })();
                        },
                      },
                    ],
                  });
                }}
              />
              {schemaExpanded && (
                <FolderNodes
                  connectionId={connectionId}
                  dbName={dbInfo.name}
                  schemaName={s.name}
                  tables={s.tables}
                  views={s.views}
                  functions={s.functions}
                  expanded={expanded}
                  toggle={toggle}
                  searchFilter={searchFilter}
                  onTableClick={onTableClick}
                  onShowContextMenu={onShowContextMenu}
                  onShowConfirmDialog={onShowConfirmDialog}
                  onShowImportModal={onShowImportModal}
                />
              )}
            </div>
          );
        });
      })()}
    </div>
  );
}

// ─── Folder nodes (Tables, Views, Functions) ─────────────────

interface FolderNodesProps {
  connectionId: string;
  dbName: string;
  schemaName: string;
  tables: { name: string; rowCountEstimate?: number; columns?: ColumnMeta[] }[];
  views: { name: string }[];
  functions: { name: string }[];
  expanded: ExpandedMap;
  toggle: (key: string) => void;
  searchFilter: string;
  onTableClick: (connectionId: string, schema: string, tableName: string, database?: string) => void;
  onShowContextMenu: (state: ContextMenuState) => void;
  onShowConfirmDialog: (state: ConfirmDialogState) => void;
  onShowImportModal: (state: {
    connectionId: string;
    schema: string;
    tableName?: string;
    tableColumns?: { name: string; type: string }[];
  }) => void;
}

function FolderNodes({
  connectionId,
  dbName,
  schemaName,
  tables,
  views,
  functions,
  expanded,
  toggle,
  searchFilter,
  onTableClick,
  onShowContextMenu,
  onShowConfirmDialog,
  onShowImportModal,
}: FolderNodesProps) {
  const openTab = useWorkspaceStore((s) => s.openTab);
  const queryClient = useQueryClient();

  // Force a bypass-cache fetch (?refresh=1) so the server hits the live DB and
  // rewrites its cache row, then seed TanStack Query with the result. A plain
  // invalidateQueries only re-runs the queryFn (no refresh=1), which the server
  // answers from its stale schema cache — so a newly-created object never shows.
  const reloadSchema = async () => {
    try {
      const fresh = await apiClient.get(
        `/api/schema/${connectionId}?database=${encodeURIComponent(dbName)}&refresh=1`,
      );
      queryClient.setQueryData(['schema', connectionId, dbName], fresh);
    } catch (err: unknown) {
      useToastStore.getState().addToast('error', err instanceof Error ? err.message : 'Reload failed');
    }
  };

  // "Show more" pagination for large folders
  const MAX_VISIBLE = 50;
  const [showAllTables, setShowAllTables] = useState(false);
  const [showAllViews, setShowAllViews] = useState(false);
  const [showAllFunctions, setShowAllFunctions] = useState(false);

  // Reset pagination when search filter changes
  useEffect(() => {
    setShowAllTables(false);
    setShowAllViews(false);
    setShowAllFunctions(false);
  }, [searchFilter]);

  const handleTableContextMenu = useCallback(
    (e: React.MouseEvent, tableName: string) => {
      e.preventDefault();
      e.stopPropagation();
      const tableData = tables.find((t) => t.name === tableName);
      const cols = tableData?.columns?.map((c) => ({ name: c.name, type: c.type }));
      const rowEstimate = tableData?.rowCountEstimate;
      const items = buildTableActions(
        schemaName,
        tableName,
        getTableActionDeps({
          connectionId,
          database: dbName,
          schema: schemaName,
          table: tableName,
          openTab,
          queryClient,
          tableColumns: cols,
          onShowImportModal,
        }),
        rowEstimate,
      );
      onShowContextMenu({ position: { x: e.clientX, y: e.clientY }, items });
    },
    [connectionId, dbName, schemaName, tables, openTab, onShowContextMenu, onShowImportModal, queryClient],
  );

  const handleViewContextMenu = useCallback(
    (e: React.MouseEvent, viewName: string) => {
      e.preventDefault();
      e.stopPropagation();
      const qualifiedName = `${schemaName}.${viewName}`;

      const items: ContextMenuItem[] = [
        {
          label: 'Open View',
          icon: Eye,
          onClick: () => onTableClick(connectionId, schemaName, viewName, dbName),
        },
        {
          label: 'Modify View...',
          icon: Pencil,
          onClick: () => {
            openTab({
              type: 'code-editor',
              id: `code-editor:${connectionId}:${schemaName}.${viewName}`,
              connectionId,
              schema: schemaName,
              objectName: viewName,
              objectType: 'view',
              mode: 'alter',
            });
          },
        },
        {
          label: 'Reload',
          icon: RefreshCw,
          onClick: () => {
            queryClient.invalidateQueries({ queryKey: ['schema', connectionId] });
          },
        },
        { label: '', separator: true },
        {
          label: 'Copy Name',
          icon: Copy,
          onClick: () => {
            navigator.clipboard.writeText(qualifiedName);
          },
        },
        { label: '', separator: true },
        {
          label: 'Drop View...',
          icon: Trash2,
          danger: true,
          onClick: () => {
            onShowConfirmDialog({
              title: 'Drop View',
              message: `This will permanently delete the view "${viewName}".`,
              confirmText: viewName,
              danger: true,
              onConfirm: async () => {
                await apiClient.post('/api/query/execute', {
                  connectionId,
                  sql: `DROP VIEW "${schemaName}"."${viewName}"`,
                });
                queryClient.invalidateQueries({ queryKey: ['schema', connectionId] });
              },
            });
          },
        },
      ];

      onShowContextMenu({ position: { x: e.clientX, y: e.clientY }, items });
    },
    [connectionId, schemaName, onTableClick, onShowContextMenu, onShowConfirmDialog, openTab, queryClient],
  );

  const handleFunctionContextMenu = useCallback(
    (e: React.MouseEvent, funcName: string) => {
      e.preventDefault();
      e.stopPropagation();
      const qualifiedName = `${schemaName}.${funcName}`;

      const items: ContextMenuItem[] = [
        {
          label: 'Modify Function...',
          icon: Pencil,
          onClick: () => {
            openTab({
              type: 'code-editor',
              id: `code-editor:${connectionId}:${schemaName}.${funcName}`,
              connectionId,
              schema: schemaName,
              objectName: funcName,
              objectType: 'function',
              mode: 'alter',
            });
          },
        },
        {
          label: 'Reload',
          icon: RefreshCw,
          onClick: () => {
            queryClient.invalidateQueries({ queryKey: ['schema', connectionId] });
          },
        },
        { label: '', separator: true },
        {
          label: 'Copy Name',
          icon: Copy,
          onClick: () => {
            navigator.clipboard.writeText(qualifiedName);
          },
        },
        { label: '', separator: true },
        {
          label: 'Drop Function...',
          icon: Trash2,
          danger: true,
          onClick: () => {
            onShowConfirmDialog({
              title: 'Drop Function',
              message: `This will permanently delete the function "${funcName}".`,
              confirmText: funcName,
              danger: true,
              onConfirm: async () => {
                await apiClient.post('/api/query/execute', {
                  connectionId,
                  sql: `DROP FUNCTION "${schemaName}"."${funcName}"`,
                });
                queryClient.invalidateQueries({ queryKey: ['schema', connectionId] });
              },
            });
          },
        },
      ];

      onShowContextMenu({ position: { x: e.clientX, y: e.clientY }, items });
    },
    [connectionId, schemaName, onShowContextMenu, onShowConfirmDialog, openTab, queryClient],
  );
  const tablesKey = `tables:${connectionId}:${dbName}:${schemaName}`;
  const viewsKey = `views:${connectionId}:${dbName}:${schemaName}`;
  const funcsKey = `funcs:${connectionId}:${dbName}:${schemaName}`;

  const isSearching = searchFilter.length > 0;
  const lowerFilter = searchFilter.toLowerCase();

  // Filter tables — also match column names
  const filteredTables = isSearching
    ? tables.filter((t) =>
        t.name.toLowerCase().includes(lowerFilter) ||
        (t as { columns?: Array<{ name: string }> }).columns?.some(
          (c) => c.name.toLowerCase().includes(lowerFilter),
        ),
      )
    : tables;
  const filteredViews = isSearching
    ? views.filter((v) => v.name.toLowerCase().includes(lowerFilter))
    : views;
  const filteredFunctions = isSearching
    ? functions.filter((f) => f.name.toLowerCase().includes(lowerFilter))
    : functions;

  return (
    <>
      {/* Tables folder */}
      {(isSearching ? filteredTables.length > 0 : tables.length > 0) && (
        <>
          <TreeNode
            label={`Tables (${filteredTables.length}${isSearching && filteredTables.length !== tables.length ? `/${tables.length}` : ''})`}
            depth={3}
            icon="folder"
            expanded={isSearching ? true : (expanded[tablesKey] ?? false)}
            hasChildren={filteredTables.length > 0}
            onToggle={() => toggle(tablesKey)}
            onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); onShowContextMenu({ position: { x: e.clientX, y: e.clientY }, items: [
              { label: 'Create Table...', icon: Plus, onClick: () => { openTab({ type: 'designer', id: `designer:${connectionId}:${schemaName}:new:${Date.now()}`, connectionId, schema: schemaName, mode: 'create' }); } },
              { label: '', separator: true },
              { label: 'Reload', icon: RefreshCw, onClick: () => { void reloadSchema(); } },
            ] }); }}
          />
          {(isSearching || (expanded[tablesKey] ?? false)) && (() => {
            const displayTables = showAllTables ? filteredTables : filteredTables.slice(0, MAX_VISIBLE);
            const hasMoreTables = filteredTables.length > MAX_VISIBLE && !showAllTables;
            return (
              <>
                {displayTables.map((t) => {
                  const tableKey = `table:${connectionId}:${schemaName}.${t.name}`;
                  // Auto-expand the table node when a column match is what
                  // brought it into view — otherwise the user sees the table
                  // name with no visible reason it matched.
                  const columnMatch = isSearching && !t.name.toLowerCase().includes(lowerFilter)
                    && (t as { columns?: Array<{ name: string }> }).columns?.some(
                      (c) => c.name.toLowerCase().includes(lowerFilter),
                    );
                  const tableExpanded = columnMatch
                    ? true
                    : (expanded[tableKey] ?? false);
                  return (
                    <div key={t.name}>
                      <TreeNode
                        label={t.name}
                        depth={4}
                        icon="table"
                        badge={t.rowCountEstimate}
                        hasChildren
                        expanded={tableExpanded}
                        highlight={searchFilter}
                        treeKey={tableKey}
                        onClick={() => {
                          // Single click: switch to existing tab if open, otherwise do nothing
                          const tabId = `table:${connectionId}:${schemaName}.${t.name}`;
                          useWorkspaceStore.getState().switchTab(tabId);
                        }}
                        onDoubleClick={() => onTableClick(connectionId, schemaName, t.name, dbName)}
                        onToggle={() => toggle(tableKey)}
                        onContextMenu={(e) => handleTableContextMenu(e, t.name)}
                      />
                      {tableExpanded && (
                        <TableColumns
                          connectionId={connectionId}
                          schemaName={schemaName}
                          tableName={t.name}
                          depth={5}
                          highlight={searchFilter}
                        />
                      )}
                    </div>
                  );
                })}
                {hasMoreTables && (
                  <div
                    className="py-[3px] px-2 cursor-pointer hover:bg-dbgo-tab-hover transition-colors"
                    style={{ paddingLeft: 5 * 12 + 4 }}
                    onClick={() => setShowAllTables(true)}
                  >
                    <span className="text-[11px] text-accent-blue">
                      Show {filteredTables.length - MAX_VISIBLE} more...
                    </span>
                  </div>
                )}
              </>
            );
          })()}
        </>
      )}

      {/* Views folder */}
      {(isSearching ? filteredViews.length > 0 : views.length > 0) && (
        <>
          <TreeNode
            label={`Views (${filteredViews.length}${isSearching && filteredViews.length !== views.length ? `/${views.length}` : ''})`}
            depth={3}
            icon="folder"
            expanded={isSearching ? true : (expanded[viewsKey] ?? false)}
            hasChildren={filteredViews.length > 0}
            onToggle={() => toggle(viewsKey)}
            onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); onShowContextMenu({ position: { x: e.clientX, y: e.clientY }, items: [
              { label: 'Create View...', icon: Eye, onClick: () => { openTab({ type: 'code-editor', id: `code-editor:${connectionId}:${schemaName}:new-view:${Date.now()}`, connectionId, schema: schemaName, objectName: 'new_view', objectType: 'view', mode: 'create' }); } },
              { label: '', separator: true },
              { label: 'Reload', icon: RefreshCw, onClick: () => { void reloadSchema(); } },
            ] }); }}
          />
          {(isSearching || (expanded[viewsKey] ?? false)) && (() => {
            const displayViews = showAllViews ? filteredViews : filteredViews.slice(0, MAX_VISIBLE);
            const hasMoreViews = filteredViews.length > MAX_VISIBLE && !showAllViews;
            return (
              <>
                {displayViews.map((v) => (
                  <TreeNode
                    key={v.name}
                    label={v.name}
                    depth={4}
                    icon="view"
                    highlight={searchFilter}
                    onClick={() => onTableClick(connectionId, schemaName, v.name, dbName)}
                    onContextMenu={(e) => handleViewContextMenu(e, v.name)}
                  />
                ))}
                {hasMoreViews && (
                  <div
                    className="py-[3px] px-2 cursor-pointer hover:bg-dbgo-tab-hover transition-colors"
                    style={{ paddingLeft: 5 * 12 + 4 }}
                    onClick={() => setShowAllViews(true)}
                  >
                    <span className="text-[11px] text-accent-blue">
                      Show {filteredViews.length - MAX_VISIBLE} more...
                    </span>
                  </div>
                )}
              </>
            );
          })()}
        </>
      )}

      {/* Functions folder */}
      {(isSearching ? filteredFunctions.length > 0 : functions.length > 0) && (
        <>
          <TreeNode
            label={`Functions (${filteredFunctions.length}${isSearching && filteredFunctions.length !== functions.length ? `/${functions.length}` : ''})`}
            depth={3}
            icon="folder"
            expanded={isSearching ? true : (expanded[funcsKey] ?? false)}
            hasChildren={filteredFunctions.length > 0}
            onToggle={() => toggle(funcsKey)}
            onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); onShowContextMenu({ position: { x: e.clientX, y: e.clientY }, items: [
              { label: 'Create Function...', icon: Braces, onClick: () => { openTab({ type: 'code-editor', id: `code-editor:${connectionId}:${schemaName}:new-func:${Date.now()}`, connectionId, schema: schemaName, objectName: 'new_function', objectType: 'function', mode: 'create' }); } },
              { label: '', separator: true },
              { label: 'Reload', icon: RefreshCw, onClick: () => { void reloadSchema(); } },
            ] }); }}
          />
          {(isSearching || (expanded[funcsKey] ?? false)) && (() => {
            const displayFunctions = showAllFunctions ? filteredFunctions : filteredFunctions.slice(0, MAX_VISIBLE);
            const hasMoreFunctions = filteredFunctions.length > MAX_VISIBLE && !showAllFunctions;
            return (
              <>
                {displayFunctions.map((f) => (
                  <TreeNode
                    key={f.name}
                    label={f.name}
                    depth={4}
                    icon="function"
                    highlight={searchFilter}
                    onContextMenu={(e) => handleFunctionContextMenu(e, f.name)}
                  />
                ))}
                {hasMoreFunctions && (
                  <div
                    className="py-[3px] px-2 cursor-pointer hover:bg-dbgo-tab-hover transition-colors"
                    style={{ paddingLeft: 5 * 12 + 4 }}
                    onClick={() => setShowAllFunctions(true)}
                  >
                    <span className="text-[11px] text-accent-blue">
                      Show {filteredFunctions.length - MAX_VISIBLE} more...
                    </span>
                  </div>
                )}
              </>
            );
          })()}
        </>
      )}
    </>
  );
}

// ─── Table columns subtree ─────────────────────────────────

interface TableColumnsProps {
  connectionId: string;
  schemaName: string;
  tableName: string;
  depth: number;
  /** Active search term — passed through to TreeNode so matching column
   *  names get highlighted in yellow. Empty string = no highlight. */
  highlight?: string;
}

function TableColumns({ connectionId, schemaName, tableName, depth, highlight }: TableColumnsProps) {
  const { data: schema } = useQuery<SchemaMetadata>({
    queryKey: ['schema', connectionId],
    queryFn: () => apiClient.get<SchemaMetadata>(`/api/schema/${connectionId}`),
    staleTime: 60_000,
  });

  const allColumns: ColumnMeta[] = useMemo(() => {
    if (!schema?.databases) return [];
    for (const db of schema.databases) {
      for (const s of db.schemas) {
        if (s.name === schemaName) {
          const table = s.tables.find((t) => t.name === tableName);
          return table?.columns ?? [];
        }
      }
    }
    return [];
  }, [schema, schemaName, tableName]);

  // While searching, keep only columns whose names contain the term — the
  // whole reason the table is expanded is to show *why* it matched, so
  // dumping every column just adds noise. If no column matches (e.g., the
  // user expanded the table manually to see what's inside), we fall back to
  // the full list so the row doesn't collapse to nothing.
  const columns: ColumnMeta[] = useMemo(() => {
    if (!highlight) return allColumns;
    const needle = highlight.toLowerCase();
    const matches = allColumns.filter((c) => c.name.toLowerCase().includes(needle));
    return matches.length > 0 ? matches : allColumns;
  }, [allColumns, highlight]);

  if (columns.length === 0) return null;

  return (
    <>
      {columns.map((col) => {
        const iconType = col.isPrimaryKey ? 'column-pk' : col.isForeignKey ? 'column-fk' : 'column';
        return (
          <TreeNode
            key={col.name}
            label={col.name}
            depth={depth}
            icon={iconType}
            badge={col.type}
            highlight={highlight}
          />
        );
      })}
    </>
  );
}

// ─── Connection error row ────────────────────────────────────
//
// Compact inline error shown under a connection whose last connect attempt
// failed. Clicking the body toggles the full message (truncated by default
// so it doesn't break the tree layout); Retry / Edit buttons give the user
// a way forward without hunting through menus.

interface ConnectionErrorRowProps {
  connectionId: string;
  message: string;
  onRetry: () => void;
  onEdit?: () => void;
}

function ConnectionErrorRow({
  connectionId: _connectionId,
  message,
  onRetry,
  onEdit,
}: ConnectionErrorRowProps) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="mx-2 my-1 px-2 py-1.5 rounded-sm bg-accent-red/10 border border-accent-red/20">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full text-left"
        title={message}
      >
        <span
          className={`text-[11px] text-accent-red ${expanded ? 'whitespace-normal break-words' : 'truncate block'}`}
        >
          {message}
        </span>
      </button>
      <div className="flex items-center gap-3 mt-1">
        <button
          type="button"
          onClick={onRetry}
          className="text-[10px] text-accent-blue hover:underline"
        >
          Retry
        </button>
        {onEdit && (
          <button
            type="button"
            onClick={onEdit}
            className="text-[10px] text-accent-blue hover:underline"
          >
            Edit connection
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Saved queries folder ────────────────────────────────────
//
// Renders the connection's saved queries as a sibling of the schema tree.
// Lives here (not in Sidebar) because queries are conceptually part of their
// connection — scoping them at the connection node makes "which prod query
// is this?" answerable at a glance.

interface SavedQueriesFolderProps {
  connectionId: string;
  expanded: ExpandedMap;
  toggle: (key: string) => void;
  searchFilter: string;
  onShowContextMenu: (state: ContextMenuState) => void;
}

function SavedQueriesFolder({
  connectionId,
  expanded,
  toggle,
  searchFilter,
  onShowContextMenu,
}: SavedQueriesFolderProps) {
  const queryClient = useQueryClient();
  const openTab = useWorkspaceStore((s) => s.openTab);
  const switchTab = useWorkspaceStore((s) => s.switchTab);
  const tabs = useWorkspaceStore((s) => s.tabs);

  const { data: allQueries = [] } = useQuery<SavedQuery[]>({
    queryKey: ['saved-queries'],
    queryFn: () => apiClient.get<SavedQuery[]>('/api/saved-queries'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/api/saved-queries/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['saved-queries'] });
    },
  });

  // Filter to this connection, then apply the sidebar search term against
  // title + SQL body so queries surface by either.
  const isSearching = searchFilter.length > 0;
  const lf = searchFilter.toLowerCase();
  const queries = allQueries
    .filter((q) => q.connectionId === connectionId)
    .filter((q) => {
      if (!isSearching) return true;
      return q.title.toLowerCase().includes(lf) || q.sql.toLowerCase().includes(lf);
    })
    .sort((a, b) => a.title.localeCompare(b.title));

  const folderKey = `saved-queries:${connectionId}`;
  // Default expanded: true. Users who collapse it get persisted state.
  // When searching, always force expand so matches are visible.
  const folderExpanded = isSearching ? true : (expanded[folderKey] ?? true);

  // Hide the folder entirely when searching returns zero matches AND the
  // connection has zero saved queries; keeps the tree clean.
  if (isSearching && queries.length === 0) return null;

  const openQuery = (sq: SavedQuery) => {
    const existingTabId = `saved-query:${sq.id}`;
    const existing = tabs.find((t) => t.id === existingTabId);
    if (existing) {
      switchTab(existingTabId);
      return;
    }
    openTab({
      type: 'query',
      id: existingTabId,
      connectionId: sq.connectionId,
      title: sq.title,
      sql: sq.sql,
      savedQueryId: sq.id,
    });
  };

  const handleQueryContextMenu = (e: React.MouseEvent, sq: SavedQuery) => {
    e.preventDefault();
    e.stopPropagation();
    onShowContextMenu({
      position: { x: e.clientX, y: e.clientY },
      items: [
        {
          label: 'Open in Editor',
          icon: FileCode2,
          onClick: () => openQuery(sq),
        },
        {
          label: 'Duplicate',
          icon: CopyPlus,
          onClick: async () => {
            try {
              await apiClient.post('/api/saved-queries', {
                connectionId: sq.connectionId,
                title: `${sq.title} (copy)`,
                sql: sq.sql,
              });
              queryClient.invalidateQueries({ queryKey: ['saved-queries'] });
            } catch (err: unknown) {
              useToastStore.getState().addToast(
                'error',
                err instanceof Error ? err.message : 'Duplicate failed',
              );
            }
          },
        },
        {
          label: 'Rename',
          icon: Pencil,
          onClick: async () => {
            const newName = await useDialogStore.getState().showPrompt({
              title: 'Rename Query',
              defaultValue: sq.title,
            });
            if (newName?.trim() && newName.trim() !== sq.title) {
              try {
                await apiClient.put(`/api/saved-queries/${sq.id}`, { title: newName.trim() });
                queryClient.invalidateQueries({ queryKey: ['saved-queries'] });
              } catch (err: unknown) {
                useToastStore.getState().addToast(
                  'error',
                  err instanceof Error ? err.message : 'Rename failed',
                );
              }
            }
          },
        },
        { label: '', separator: true },
        {
          label: 'Delete',
          icon: Trash2,
          danger: true,
          onClick: () => deleteMutation.mutate(sq.id),
        },
      ],
    });
  };

  return (
    <div>
      <TreeNode
        label="Saved Queries"
        depth={1}
        icon="saved-queries-folder"
        expanded={folderExpanded}
        hasChildren={queries.length > 0}
        badge={queries.length > 0 ? queries.length : undefined}
        onToggle={() => toggle(folderKey)}
      />
      {folderExpanded && queries.length === 0 && !isSearching && (
        <div className="pl-10 py-1">
          <span className="text-[11px] text-text-muted italic">
            No saved queries
          </span>
        </div>
      )}
      {folderExpanded && queries.map((sq) => (
        <TreeNode
          key={sq.id}
          label={sq.title}
          depth={2}
          icon="saved-query"
          onClick={() => openQuery(sq)}
          onContextMenu={(e) => handleQueryContextMenu(e, sq)}
        />
      ))}
    </div>
  );
}
